import { createSlice } from '@reduxjs/toolkit';

// A marker is the map's view of a device: where it is, which way it points,
// and how to colour it. The server sends one per device with a position, in
// the shape of GET /api/devices/markers. It is stored here as a position-like
// record carrying the device fields the map layer reads, so the marker layer
// can draw it without holding the device or its full position.
const toRecord = (marker) => ({
  id: marker.positionId,
  deviceId: marker.deviceId,
  latitude: marker.latitude,
  longitude: marker.longitude,
  course: marker.course,
  fixTime: marker.fixTime,
  name: marker.name,
  status: marker.status,
  category: marker.category,
  disabled: marker.disabled === true,
  attributes: {},
});

const { reducer, actions } = createSlice({
  name: 'markers',
  initialState: {
    items: {},
    loaded: false,
    // Whether the index was fetched under a filter. The socket streams a
    // marker for any device that reports, filtered or not, so under a
    // filter only devices already in the index take updates; without one,
    // any active device does, which lets a new device appear.
    filtered: false,
    // Devices the socket announced as newly visible since the index was
    // fetched. Under a filter they were only announced because they match,
    // so their markers are taken like those of indexed devices.
    admitted: {},
  },
  reducers: {
    refresh(state, action) {
      const { markers, filtered = false } = action.payload;
      state.items = {};
      markers.forEach((marker) => {
        state.items[marker.deviceId] = toRecord(marker);
      });
      state.loaded = true;
      state.filtered = filtered;
      state.admitted = {};
    },
    admit(state, action) {
      action.payload.forEach((id) => {
        state.admitted[id] = true;
      });
    },
    update(state, action) {
      action.payload.forEach((marker) => {
        const known = Object.prototype.hasOwnProperty.call(state.items, marker.deviceId)
          || state.admitted[marker.deviceId] === true;
        if (known || (!state.filtered && marker.disabled !== true)) {
          state.items[marker.deviceId] = toRecord(marker);
        }
      });
    },
    remove(state, action) {
      const ids = Array.isArray(action.payload) ? action.payload : [action.payload];
      ids.forEach((id) => {
        delete state.items[id];
        delete state.admitted[id];
      });
    },
  },
});

export { actions as markersActions };
export { reducer as markersReducer };
